import React from "react";
import commonStyles from "../../../containers/settings/settingsEditCommon.css";
import { L10n } from "@fidget/common/i18n";
import { intl } from "@fidget/common/i18n/TypedIntl";
import { SubtitleDescription } from "../subtitleDescription";
import { observer } from "mobx-react";
import { FormPlaceholder } from "../settingsForms/fullWidthPlaceholder/formPlaceholder";
import { RoleList } from "./roleList";
import { RolesSettingsProvider, useRolesSettings } from "./useRolesSettingsState";
import { RoleEdit } from "./edit/roleEdit";
import { withPreloading } from "../../HOC/withPreloading/withPreloading";
import { RoleForm } from "./types";
import { SettingsListFilterProvider } from "../settingsForms/filters/useListFilter/useListFilter";
import { EditInput } from "@fidget/common/components/form/editInput";
import styles from "./rolesSettings.css";
import classNames from "classnames";
import { useStore } from "../../../models";
import { VerticalSeparator } from "../settingsForms/separators/verticalSeparator/verticalSeparator";
import { ElementFormActions } from "../settingsForms/elementForm/actions/elementFormActions";
import { ElementFormSaveDiscardIcon } from "../settingsForms/elementForm/icons/elementFormSaveDiscardIcon";
import { ElementForm } from "../settingsForms/elementForm/elementForm/elementForm";
import settingStyles from "../../../styles/settings.css";
import { logger } from "@fidget/common/utils";
import { mapFormToUpdateForm, copyExistingForm } from "./utils";
import { useAgentServices } from "../../../models/agentServices";

const RolesSettingsContainer = observer(() => {
  const { uiStore, reportsStore, inboxStore, roleStore } = useStore();
  const { spinnerService } = useAgentServices();

  const settings = useRolesSettings();
  const { currentlyEditedItem } = settings;

  const handleCreateRoleClick = () => {
    settings.handleCreateNewClick();
  };

  const handleUpdateRole = async (item: RoleForm) => {
    try {
      const role = await spinnerService.updateRole(item.$.id.$, mapFormToUpdateForm(item));
      roleStore.updateRole(role);

      // for display conversations permissions
      await inboxStore.invalidate();
      await settings.updateItem(copyExistingForm(item));
      reportsStore.prepare();
    } catch (e) {
      logger.error({ message: "Couldn't update role", error: e });
      uiStore.addGenericErrorToast();
    }
  };

  const handleDeleteRole = async (id: string) => {
    try {
      await settings.apiDeleteItem(id);
      settings.uiRemoveExistingItem(id);
    } catch (e) {
      uiStore.addGenericErrorToast();
    }
  };

  const renderNewItemForm = () => {
    return (
      <ElementForm isEdited>
        <div className={styles.content}>
          <span className={classNames(settingStyles["form-label-font"])}>
            {intl.formatMessage("settings.rolesSettings.label")}
          </span>
          <EditInput
            placeholder={intl.formatMessage("settings.rolesSettings.createRole.placeholder")}
            className={classNames(styles.flex, styles.center, styles["new-role-input"])}
            autoFocus
            onChange={(event) =>
              settings.newItem?.$.name.reset((event.target as HTMLInputElement).value)
            }
          />
        </div>
        <VerticalSeparator />
        <ElementFormActions>
          <ElementFormSaveDiscardIcon
            onSave={settings.saveNewItem}
            onDiscard={settings.discardNewItem}
          />
        </ElementFormActions>
      </ElementForm>
    );
  };

  const renderAddNew = () => {
    return settings.newItem ? (
      renderNewItemForm()
    ) : (
      <FormPlaceholder
        onClick={handleCreateRoleClick}
        text={intl.formatMessage("settings.rolesSettings.createRole")}
      />
    );
  };

  const renderRoleList = () => (
    <RoleList
      onDelete={handleDeleteRole}
      onEdit={settings.onExistingEditClick}
      roles={settings.items}
    />
  );

  const renderSettings = () => (
    <div className={styles.form}>
      <div className={commonStyles.title}>
        <L10n id="settings.rolesSettings" />
      </div>

      <div className={commonStyles.section}>
        <SubtitleDescription
          subtitle={intl.formatMessage("settings.rolesSettings.list")}
          description={intl.formatMessage("settings.rolesSettings.description")}
        />

        {renderAddNew()}
        {renderRoleList()}
      </div>
    </div>
  );

  return currentlyEditedItem ? (
    <RoleEdit
      save={handleUpdateRole}
      onBack={settings.discardEditedItem}
      role={currentlyEditedItem}
    />
  ) : (
    renderSettings()
  );
});

export const RolesSettings = withPreloading(() => (
  <SettingsListFilterProvider<RoleForm>>
    <RolesSettingsProvider>
      <RolesSettingsContainer />
    </RolesSettingsProvider>
  </SettingsListFilterProvider>
));
